"use client";
import QuoteTabBar from "@/components/views/quote-venue/QuoteTabBar";
import QuoteBottomBar from "@/components/views/quote-venue/QuoteBottomBar";
import { useEffect, useMemo, useState } from "react";
import apiFetch from "@/lib/fetch";
import toast from "react-hot-toast";
import CombinedInitialScreen from "@/components/views/quote-venue/steps/CombinedInitialScreen";
import RegisterScreen from "@/components/views/quote-venue/steps/RegisterScreen";
import GenderScreen from "@/components/views/quote-venue/steps/GenderScreen";
import ThanksScreen from "@/components/views/quote-venue/steps/ThanksScreen";
import InterestScreen from "@/components/views/quote-venue/steps/InterestScreen";
import { useSession } from "next-auth/react";
import { useTranslation } from "@/hooks/translationContext";
import { useDataUpdater } from "@/lib/hooks/useApi";
import httpErrorMessage from "@/lib/http-error-message";
import { usePlausible } from "next-plausible";
import { PLAUSIBLE_GOAL_KEYS } from "@/@core/constants/plausible";
import { GetQuoteProvider } from "@/context/quote-venue/GetQuoteData";
import { sendGAEvent } from '@next/third-parties/google'

interface Props {
  service: any;
  district: any;
  venueCount: number;
}

export default function QuoteVenue(props: Props) {
  const { t } = useTranslation();
  const plausible = usePlausible();
  const {
    data: sessionData,
    status: sessionStatus,
    update: updateSessionData,
  } = useSession();

  const [activeStep, setActiveStep] = useState(0);
  const [data, setData] = useState({
    selectedDistrict: props.district || {},
    selectedService: props.district ? props.service : {},
  });

  const [errorMessage, setErrorMessage] = useState("");

  const [userData, setUserData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    gender: "",
    isAgreed: true,
  });
  const [interestedDistricts, setInterestedDistricts] = useState([]);
  const [interestedServices, setInterestedServices] = useState([]);
  const [validSteps, setValidSteps] = useState({
    initialScreen: false,
    registerScreen: false,
    genderScreen: false,
  });

  const stepOneTitleKey = "quote-page-step-one-title";
  const stepOneTitle = t(stepOneTitleKey);
  const stepDescription = t("quote-page-step-description");

  const getStepsArray = () => {
    return [
      {
        title:
          stepOneTitle == stepOneTitleKey
            ? t("quote-page-question-title")
            : stepOneTitle,
        description: stepDescription,
      },
      {
        title: t("quote-page-ask-password-step-title", {
          defaultValue: "Email adresinizi ve şifrenizi girin"
        }),
        description: "",
      },
      {
        title: t("account-interests-title"),
        description: stepDescription,
      },
      {
        title: "Cinsiyetiniz",
        description: stepDescription,
      },
      {
        title: t("quote-page-thanks-title"),
        description: t("quote-page-congratulations-message"),
      },
    ];
  };
  const plausibleProps = useMemo(() => {
    return { newDesign: true, isLoggedIn: sessionStatus };
  }, [sessionStatus]);

  const [stepsArray, setStepsArray] = useState(getStepsArray());

  const stepComponents = {
    0: (
      <CombinedInitialScreen
        setErrorMessage={setErrorMessage}
        setValidStep={(state: boolean) =>
          setValidSteps({ ...validSteps, initialScreen: state })
        }
        userData={userData}
        setUserData={setUserData}
      />
    ),
    1: (
      <RegisterScreen
        setValidStep={(state: boolean) =>
          setValidSteps({ ...validSteps, registerScreen: state })
        }
        data={userData}
        setData={setUserData}
      />
    ),
    2: (
      <InterestScreen
        setSelectedServices={setInterestedServices}
        setSelectedDistricts={setInterestedDistricts}
        selectedServices={interestedServices}
        selectedDistricts={interestedDistricts}
      />
    ),
    3: (
      <GenderScreen
        setValidStep={(state: boolean) =>
          setValidSteps({ ...validSteps, genderScreen: state })
        }
        gender={userData.gender}
        setGender={(gender) => {
          return setUserData((prev) => {
            return {
              ...prev,
              gender,
            };
          });
        }}
      />
    ),
    4: <ThanksScreen />,
  };

  const { sendRequest: changePhoneNumber } = useDataUpdater({
    auth: true,
    path: `user/change-phone-number`,
    method: "PUT",
    body: {
      phone: userData?.phone,
    },
  });

  useEffect(() => {
    setStepsArray(getStepsArray());
  }, [data]);

  useEffect(() => {
    if (data?.selectedDistrict?.id && activeStep == 0) {
      setData((prev) => ({
        ...prev,
        selectedService:
          prev.selectedService?.id && prev.selectedDistrict?.id === data?.selectedDistrict?.id
            ? prev.selectedService
            : {},
      }));
    }
  }, [data?.selectedDistrict]);

  useEffect(() => {
    plausible(`request_quote_step_one_view`, {
      props: plausibleProps,
    });
  }, []);

  useEffect(() => {
    if (sessionStatus == "authenticated") {
      setUserData((prevState) => {
        return {
          ...prevState,
          name: sessionData.user?.firstname,
          email: sessionData?.user?.email,
          phone: sessionData?.user?.phone || "",
        };
      });
    }
  }, [sessionStatus]);

  const sendStepEvent = (step: number) => {
    plausible(`request_quote_step_${step}`, {
      props: plausibleProps,
    });
  };

  async function handleRegisterRequest() {
    setValidSteps({ ...validSteps, genderScreen: false });

    const body = {
      nameAndSurname: userData.name,
      email: userData.email,
      phone: userData.phone,
      gender: userData.gender == "male" ? 0 : 1,
      interest: {
        districts:
          interestedDistricts.map((district: any) => district.id) || [],
        services: interestedServices.map((service: any) => service.id) || [],
      },
      password: userData.password,
      section: "quote",
    };

    const toastId = toast.loading(
      t("quote-page-register-loading"),
    );
    const response = await apiFetch({
      route: "user",
      method: "POST",
      body,
    });

    if (response?.id) {
      sendStepEvent(4);
      toast.remove(toastId);
      toast.success(t("quote-page-register-success"));
      setActiveStep(4);
    } else {
      setValidSteps({ ...validSteps, genderScreen: true });
      toast.remove(toastId);
      toast.error(
        response?.message || t("quote-page-register-error"),
      );
    }
  }

  async function handleOfferRequest() {
    setValidSteps({ ...validSteps, initialScreen: false });

    const toastId = toast.loading(
      t("quote-page-notification-loading-message", {
        defaultValue: "Lütfen bekleyiniz...",
      }),
    );

    let userInformation = {};
    const session = sessionData as any;

    if (sessionStatus === "unauthenticated") {
      userInformation = {
        name: userData.name,
        phone: userData.phone,
      };
    } else {
      userInformation = {
        name:
          session?.user?.firstname + " " + session?.user?.lastname,
        phone:
          session?.user?.phone?.length > 0
            ? session?.user?.phone
            : userData.phone,
      };
    }

    if (sessionStatus == "authenticated") {
      const phoneNumberResult =
        await changePhoneNumber().catch(httpErrorMessage);
      if (phoneNumberResult !== true) {
        toast.error(t("quote-page-phone-save-error"));
        toast.remove(toastId);

        return;
      } else {
        const sessionData = await updateSessionData({
          phone: userData?.phone,
        });
        if (sessionData.user?.phone === userData?.phone) {
          toast.success(t("quote-page-phone-save-success"));
        }
      }
    }

    const quoteData = {
      ...userInformation,
      service: data.selectedService?.id,
      district: data.selectedDistrict?.id,
    };

    const response = await apiFetch({
      route: "quote",
      method: "POST",
      body: quoteData,
      handleErrorSilently: false
    });

    if (response == true) {
      sendStepEvent(2);
      plausible(PLAUSIBLE_GOAL_KEYS.REQUEST_QUOTE_COMPETED_EVENT, {
        props: plausibleProps,
      });

      sendGAEvent('event', 'formSubmission', {
        formName: 'quoteForm',
        formStatus: 'completed',
        ...quoteData,
      });

      toast.remove(toastId);
      if (sessionStatus == "authenticated") {
        setActiveStep(4);
      } else {
        setActiveStep(1);
      }

      toast.success(
        t("quote-page-notification-success-message", {
          defaultValue: "İşlem başarıyla gerçekleştirildi",
        }),
      );
    } else {
      plausible(PLAUSIBLE_GOAL_KEYS.REQUEST_QUOTE_FAILED_EVENT, {
        props: {
          message: response?.message || t("quote-page-submit-error"),
          ...quoteData,
          ...plausibleProps,
        },
      });

      sendGAEvent('event', 'formSubmission', {
        formName: 'quoteForm',
        formStatus: 'failed',
        ...quoteData,
      });

      toast.remove(toastId);
      setValidSteps({ ...validSteps, initialScreen: true });
      toast.error(response?.message || t("quote-page-submit-error"));
    }
  }

  const stepApiCalls = {
    0: handleOfferRequest,
    3: handleRegisterRequest,
  };

  function handleNext() {
    // @ts-ignore
    const handler = stepApiCalls[activeStep];

    if (typeof handler === "function") {
      handler();
    } else {
      sendStepEvent(activeStep + 1);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }

  function handlePrev() {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function isLastStep() {
    return activeStep === stepsArray.length - 1;
  }

  const isThisValidStep = (step: number) => {
    switch (step) {
      case 0:
        return validSteps.initialScreen;
      case 1:
        return validSteps.registerScreen;
      case 2:
        return true;
      case 3:
        return validSteps.genderScreen;
      default:
        return false;
    }
  };

  const getComponent = () => {
    // @ts-ignore
    return stepComponents[activeStep];
  };

  const handleChange = (type, key, value) => {
    if (type == "data") {
      setData({ ...data, [key]: value });
    } else {
      setUserData({ ...userData, [key]: value });
    }
  };

  return (
    <div className="relative px-6 md:pb-4 md:w-[414px] h-full w-full bg-white md:rounded-3xl flex-col justify-start items-start flex">
      <div className="mb-10 w-full">
        <QuoteTabBar steps={stepsArray} activeStep={activeStep} />
        <div className="absolute left-0 mt-4 w-full h-0.5 bg-gray-200" />
      </div>

      <div className="flex flex-1 md:pb-10 w-full">
        <GetQuoteProvider
          data={data}
          userData={userData}
          onChange={handleChange}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        >
          {getComponent()}
        </GetQuoteProvider>
      </div>

      <QuoteBottomBar
        errorMessage={errorMessage}
        isThisValidStep={isThisValidStep}
        isLastStep={isLastStep()}
        activeStep={activeStep}
        handleNext={handleNext}
        handlePrev={handlePrev}
      />
    </div>
  );
}
